if (!customElements.get("cart-upsell")) {
  class CartUpsell extends HTMLElement {
    constructor() {
      super();
      this.track = null;
      this.template = null;
      this.slider = null;
      this.drawer = null;
      this.fetchAbort = null;
      this.cartUpdateEvent =
        (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) ||
        "cart-update";
      this.onCartUpdate = this.onCartUpdate.bind(this);
      this.onLookChange = this.onLookChange.bind(this);
      this.onLookClick = this.onLookClick.bind(this);
      this.onLookSelectKeydown = this.onLookSelectKeydown.bind(this);
    }

    connectedCallback() {
      this.cacheElements();
      this.bindDrawerRefresh();
      document.addEventListener(this.cartUpdateEvent, this.onCartUpdate);
      this.addEventListener("change", this.onLookChange);
      this.addEventListener("click", this.onLookClick);
      this.setupLookControls();
      this.refresh();
    }

    disconnectedCallback() {
      document.removeEventListener(this.cartUpdateEvent, this.onCartUpdate);
      this.removeEventListener("change", this.onLookChange);
      this.removeEventListener("click", this.onLookClick);
      this.querySelectorAll("[data-etoile-look-select]").forEach((select) => {
        select.removeEventListener("keydown", this.onLookSelectKeydown);
      });
      if (this.fetchAbort) {
        this.fetchAbort.abort();
        this.fetchAbort = null;
      }
    }

    cacheElements() {
      this.track = this.querySelector("[data-upsell-track]");
      this.slider = this.querySelector("slider-component");
      this.drawer = this.closest("cart-drawer");
    }

    setupLookControls() {
      this.querySelectorAll("[data-etoile-look-select]").forEach((select) => {
        select.addEventListener("keydown", this.onLookSelectKeydown);
      });
      const toggle = this.querySelector("[data-etoile-look-pause]");
      if (!toggle || !this.track) return;
      const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      toggle.hidden = this.track.children.length < 2 || !!reduced;
    }

    onLookSelectKeydown(event) {
      if (event.key === "ArrowLeft" || event.key === "ArrowRight" || event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.stopPropagation();
      }
    }

    onLookChange(event) {
      const select = event.target;
      if (!select || !select.matches || !select.matches("[data-etoile-look-select]")) return;
      const card = select.closest(".cart-drawer__upsell-card");
      if (!card) return;
      const button = card.querySelector("[data-etoile-quick-add]");
      const option = select.options[select.selectedIndex];
      if (button) {
        button.disabled = !select.value || !option || option.disabled;
        button.setAttribute("data-variant-id", select.value || "");
      }
      const price = card.querySelector("[data-etoile-look-price]");
      if (price && option && option.dataset.price) price.textContent = option.dataset.price;
      const image = card.querySelector(".cart-drawer__upsell-media img");
      if (image && option && option.dataset.image) {
        image.removeAttribute("srcset");
        image.src = option.dataset.image;
      }
      const imageNote = card.querySelector("[data-etoile-look-image-note]");
      if (imageNote) imageNote.hidden = !(option && option.dataset.imageNote === "true");
    }

    onLookClick(event) {
      const toggle = event.target && event.target.closest ? event.target.closest("[data-etoile-look-pause]") : null;
      if (!toggle || !this.contains(toggle) || !this.slider) return;
      const paused = toggle.getAttribute("aria-pressed") === "true";
      if (paused) {
        this.slider.autoplayEnabled = true;
        this.slider.autoplayPaused = false;
        if (typeof this.slider.startAutoplay === "function") this.slider.startAutoplay();
        if (this.slider.contains(document.activeElement) && typeof this.slider.pauseAutoplay === "function") this.slider.pauseAutoplay();
      } else {
        this.slider.autoplayEnabled = false;
        if (typeof this.slider.stopAutoplay === "function") this.slider.stopAutoplay();
      }
      toggle.setAttribute("aria-pressed", paused ? "false" : "true");
      toggle.setAttribute("aria-label", paused ? toggle.dataset.pauseLabel : toggle.dataset.playLabel);
      const symbol = toggle.querySelector("span");
      if (symbol) symbol.textContent = paused ? "Ⅱ" : "▶";
    }

    bindDrawerRefresh() {
      if (!this.drawer) return;
      if (typeof this.drawer.refreshUpsell === "function") return;
      this.drawer.refreshUpsell = () => {
        const upsell = this.drawer.querySelector("cart-upsell[data-upsell]");
        if (upsell && typeof upsell.refresh === "function") {
          upsell.refresh();
        }
      };
    }

    async refresh() {
      this.cacheElements();
      if (!this.track) return;
      const mode = this.dataset.upsellMode || "manual";
      if (mode !== "auto") return;

      const productId = this.dataset.upsellProductId;
      if (!productId) {
        this.classList.add("hidden");
        return;
      }

      const limit = Number.parseInt(this.dataset.upsellLimit || "3", 10) || 3;
      await this.loadRecommendations(productId, limit);
    }

    async onCartUpdate(event) {
      const variantId = event && event.detail ? event.detail.variantId : null;
      if (!variantId) return;
      await this.updateLatestProductId(variantId);
    }

    async updateLatestProductId(variantId) {
      try {
        const response = await fetch("/cart.js", { credentials: "same-origin" });
        if (!response.ok) return;
        const data = await response.json();
        const item = data && Array.isArray(data.items)
          ? data.items.find(
              (entry) => String(entry.variant_id) === String(variantId)
            )
          : null;
        if (!item || !item.product_id) return;
        this.dataset.upsellProductId = String(item.product_id);
        const mode = this.dataset.upsellMode || "manual";
        if (mode === "auto") {
          await this.refresh();
        }
      } catch (error) {
        // noop
      }
    }

    async availableFallbackHtml(fallback, signal) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
      const cards = Array.from(fallback.content.children);
      const rendered = await Promise.all(cards.map(async (card) => {
        const handle = card.dataset.etoileProductHandle;
        const id = card.dataset.etoileProductId;
        if (!handle || !id) return "";
        try {
          const response = await fetch(root.replace(/\/?$/, "/") + "products/" + encodeURIComponent(handle) + ".js", {
            signal, credentials: "same-origin", headers: { "Accept": "application/json" },
          });
          if (!response.ok) return "";
          const product = await response.json();
          if (!product || String(product.id) !== String(id) || !product.available) return "";
          return card.outerHTML;
        } catch (error) {
          if (error && error.name === "AbortError") throw error;
          return "";
        }
      }));
      return rendered.join("");
    }

    async loadRecommendations(productId, limit) {
      if (!this.track) return;
      if (this.fetchAbort) {
        this.fetchAbort.abort();
      }
      const controller = new AbortController();
      this.fetchAbort = controller;
      try {
        const rawUrl = this.dataset.url || "";
        if (!rawUrl) throw new Error("Upsell URL missing.");
        const url = new URL(rawUrl, window.location.origin);
        url.searchParams.set("product_id", productId);
        url.searchParams.set("limit", String(limit));
        const sectionId =
          this.dataset.upsellSectionId || "cart-upsell-recommendations";
        url.searchParams.set("section_id", sectionId);
        // Prefer the existing product/variant-specific PDP styling selection.
        // Never substitute same-type related products when styling is missing.
        const intents = ["complementary"];
        let items = null;
        const fallback = this.querySelector("template[data-etoile-cart-fallback]");
        if (fallback && fallback.dataset.productId === String(productId) &&
            fallback.content && fallback.content.children.length > 0) {
          const fallbackHtml = await this.availableFallbackHtml(fallback, controller.signal);
          if (fallbackHtml) items = { innerHTML: fallbackHtml };
        }
        for (const intent of intents) {
          if (items) break;
          url.searchParams.set("intent", intent);
          try {
            const fetchHtml = window.webvista && window.webvista.fetchHtml;
            const parsed = fetchHtml
              ? await fetchHtml(url.toString(), controller.signal)
              : new DOMParser().parseFromString(
                  await fetch(url.toString(), { signal: controller.signal }).then(
                    (response) => {
                      if (!response.ok) throw new Error("Recommendations unavailable");
                      return response.text();
                    }
                  ),
                  "text/html"
                );
            const candidate = parsed.querySelector("[data-upsell-items]");
            if (candidate && candidate.children.length > 0) {
              items = candidate;
              break;
            }
          } catch (requestError) {
            if (requestError && requestError.name === "AbortError") throw requestError;
          }
        }
        if (controller.signal.aborted || this.fetchAbort !== controller || !this.isConnected) return;
        if (!items) {
          this.classList.toggle("hidden", !this.track.children.length);
          return;
        }
        this.classList.remove("hidden");
        this.track.innerHTML = items.innerHTML;
        if (window.webvista && window.webvista.initLazyImageAnimation) {
          window.webvista.initLazyImageAnimation(this.track);
        }
        this.resetSlider();
        this.setupLookControls();
      } catch (error) {
        if (error && error.name === "AbortError") return;
        this.classList.toggle("hidden", !this.track || !this.track.children.length);
      }
    }

    resetSlider() {
      if (!this.slider) return;
      if (typeof this.slider.disconnectedCallback === "function") {
        this.slider.disconnectedCallback();
      }
      if (this.track) this.track.style.overflowX = "";
      if (typeof this.slider.connectedCallback === "function") {
        this.slider.connectedCallback();
      }
    }

  }

  customElements.define("cart-upsell", CartUpsell);
}
